import { type ResolvedConfig, resolveConfig } from 'ProdantixSdk/core/config';
import { ProdantixError } from 'ProdantixSdk/core/errors';
import { buildEnvelope } from 'ProdantixSdk/core/events';
import { assignVariantIn, evaluateAll, evaluateFlagIn, type FlagRule, type FlagSnapshot, snapshotNeedsCohorts } from 'ProdantixSdk/core/flag-eval';
import { FlagStream } from 'ProdantixSdk/core/flag-stream';
import { FlagsClient } from 'ProdantixSdk/core/flags';
import { IdentityManager } from 'ProdantixSdk/core/identity';
import { MessagesClient } from 'ProdantixSdk/core/messages';
import type { FlagEvalContext } from 'ProdantixSdk/core/predicate';
import { EventQueue } from 'ProdantixSdk/core/queue';
import { SessionManager } from 'ProdantixSdk/core/session';
import { SYSTEM_EVENT } from 'ProdantixSdk/core/system-events';
import { isoTimestamp } from 'ProdantixSdk/core/time';
import { FetchTransport } from 'ProdantixSdk/core/transport';
import type { Consent, EventContext, EventEnvelope, EventProperties, FlagVariant, InboxMessage, ProdantixConfig, Transport } from 'ProdantixSdk/core/types';

const PERSIST_SUFFIX = 'queue';
const SERVER_BATCH_LIMIT = 1000;
// Matches the snapshot endpoint's `Cache-Control: max-age=30`.
const SNAPSHOT_TTL_MS = 30_000;

export interface LocalFlagContext {
  attributes?: EventProperties;
  /** The cohorts the current distinct id belongs to. When omitted and the
   *  snapshot names a cohort, the client fetches them once per distinct id. */
  cohorts?: string[];
  properties?: EventProperties;
}

export interface CaptureOptions {
  /** Speak for someone other than this client's own identity. A server has no
   *  visitor of its own to lend an event, so an event it sends on a browser's
   *  behalf names the person directly. */
  distinctId?: string;
  properties?: EventProperties;
  sessionId?: string;
  timestamp?: Date;
}

/**
 * The profile keys the platform itself understands.
 *
 * They are ordinary properties on the wire; the only thing that makes them
 * special is the `$` prefix, which is how this platform marks a name it owns
 * rather than one the caller chose. Passing them through `traits` means never
 * having to remember whether the console reads `email` or `$email`: an
 * unprefixed `email` is a property of your own and stays one, and would never
 * reach the Email column.
 */
export interface PersonTraits {
  avatar?: string;
  email?: string;
  name?: string;
  phone?: string;
}

/** Ports `PersonTraits` onto the reserved wire keys. */
const traitProperties = (traits: PersonTraits): EventProperties => {
  const properties: EventProperties = {};
  if (traits.avatar !== undefined) properties.$avatar = traits.avatar;
  if (traits.email !== undefined) properties.$email = traits.email;
  if (traits.name !== undefined) properties.$name = traits.name;
  if (traits.phone !== undefined) properties.$phone = traits.phone;
  return properties;
};

export interface IdentifyOptions {
  /** Arbitrary profile properties your own code defines. */
  set?: EventProperties;
  /** The reserved traits the console renders as fields. */
  traits?: PersonTraits;
}

export class ProdantixClient {
  private readonly config: ResolvedConfig;
  private readonly identity: IdentityManager;
  private readonly session?: SessionManager;
  private readonly queue: EventQueue;
  private readonly transport: Transport;
  private readonly flagsClient: FlagsClient;
  private readonly messagesClient: MessagesClient;
  private readonly context: EventContext;
  private flushTimer?: ReturnType<typeof setInterval>;
  private flushing = false;
  private cachedFlags?: { distinctId: string; flags: Record<string, boolean>; variants: Record<string, FlagVariant> };
  private cachedSnapshot?: { fetchedAt: number; snapshot: FlagSnapshot };
  private membershipCache?: { cohorts: string[]; distinctId: string; fetchedAt: number };
  private stream?: FlagStream;
  private shutDown = false;
  /** One `$feature_flag_called` per (distinct id, flag, response) per client
   *  lifetime: the experiment engine attributes on the FIRST exposure, so a
   *  second event for the same answer would only cost the project an event. */
  private readonly exposures = new Set<string>();

  constructor(config: ProdantixConfig) {
    this.config = resolveConfig(config);
    this.identity = new IdentityManager(this.config.storage, this.config.sharedStorage, this.config.ids, this.config.namespace);
    if (this.config.consent !== undefined) {
      this.identity.setConsent(this.config.consent);
    }
    this.session = this.config.sessionTracking ? new SessionManager(this.config.sharedStorage, this.config.ids, this.config.clock, this.config.namespace) : undefined;
    this.queue = new EventQueue(this.config.maxQueueSize, ({ dropped }) => {
      this.config.onError(new ProdantixError('queue_overflow', `event queue overflow: dropped ${dropped.length} oldest event(s)`));
    });
    this.transport =
      this.config.transport ??
      new FetchTransport({
        maxRetries: this.config.maxRetries,
        requestTimeoutMs: this.config.requestTimeoutMs,
      });
    this.flagsClient = new FlagsClient({
      host: this.config.flagsHost,
      projectKey: this.config.apiKey,
      requestTimeoutMs: this.config.requestTimeoutMs,
    });
    this.messagesClient = new MessagesClient({
      host: this.config.flagsHost,
      projectKey: this.config.apiKey,
      requestTimeoutMs: this.config.requestTimeoutMs,
    });
    this.context = {
      sdk: this.config.sdkName,
      sdk_version: this.config.sdkVersion,
    };
    if (this.config.os) {
      this.context.os = this.config.os;
    }
    if (this.config.locale) {
      this.context.locale = this.config.locale;
    }
    this.restoreQueue();
    this.startTimer();
  }

  get distinctId(): string {
    return this.identity.getDistinctId();
  }

  get anonymousId(): string {
    return this.identity.getAnonymousId();
  }

  /** Whether this client still holds a person, from the persisted distinct id,
   *  so a fresh page load answers the same as the load that identified them.
   *  Public because a surface that has resolved to nobody must reset the client
   *  only when there is someone to reset: a sign-out is a hard navigation on a
   *  BFF surface, so no value-to-null transition ever renders. */
  get isIdentified(): boolean {
    return this.identity.isIdentified();
  }

  get sessionId(): string | undefined {
    return this.session?.current();
  }

  get sessionStartedAt(): number | undefined {
    return this.session?.startedAt();
  }

  capture(eventName: string, options: CaptureOptions = {}): void {
    try {
      this.identity.refresh();
      const properties = {
        ...this.config.defaultProperties(),
        ...(options.properties ?? {}),
      };
      const envelope = buildEnvelope(
        {
          context: this.context,
          distinctId: options.distinctId ?? this.identity.getDistinctId(),
          eventName,
          properties,
          sessionId: options.sessionId ?? this.session?.current(),
          timestamp: options.timestamp,
        },
        this.config.clock,
        this.config.ids,
      );
      this.enqueue(envelope);
    } catch (error) {
      this.config.onError(error);
    }
  }

  identify(distinctId: string, options: IdentifyOptions = {}): void {
    this.identity.refresh();
    const result = this.identity.identify(distinctId);
    const properties: EventProperties = {};
    if (result.anonymousToLink !== undefined) {
      properties.$anon_distinct_id = result.anonymousToLink;
    }
    const set = { ...traitProperties(options.traits ?? {}), ...(options.set ?? {}) };
    if (Object.keys(set).length > 0) {
      properties.$set = set;
    }
    this.capture(SYSTEM_EVENT.identify, { properties });
  }

  /**
   * Join a browser's anonymous trail to a person from a server. A sign-in that
   * completes in a redirect renders no page, so the browser's own `identify`
   * never runs there; this sends the one `$identify` it would have sent, for
   * the ids given, and leaves this client's own identity as it was.
   */
  link(distinctId: string, anonymousId: string, traits?: PersonTraits): void {
    const properties: EventProperties = { $anon_distinct_id: anonymousId };
    const set = traitProperties(traits ?? {});
    if (Object.keys(set).length > 0) {
      properties.$set = set;
    }
    this.capture(SYSTEM_EVENT.identify, { distinctId, properties });
  }

  /**
   * Record the visitor's answer about the join. It rides the shared store, so
   * every subdomain and the login's server read the same one, and it takes
   * effect on the next identify and the next decorated click; nothing already
   * joined is unjoined.
   */
  setConsent(consent: Consent): void {
    this.identity.setConsent(consent);
  }

  /** The visitor's answer, or null when they have not been asked. */
  consent(): Consent | null {
    return this.identity.consent();
  }

  alias(alias: string): void {
    this.capture(SYSTEM_EVENT.createAlias, { properties: { alias } });
  }

  group(groupType: string, groupKey: string, properties?: EventProperties): void {
    const payload: EventProperties = {
      $group_key: groupKey,
      $group_type: groupType,
    };
    if (properties) {
      payload.$set = properties;
    }
    this.capture(SYSTEM_EVENT.group, { properties: payload });
  }

  setPersonProperties(set?: EventProperties, setOnce?: EventProperties): void {
    const properties: EventProperties = {};
    if (set) {
      properties.$set = set;
    }
    if (setOnce) {
      properties.$set_once = setOnce;
    }
    this.capture(SYSTEM_EVENT.set, { properties });
  }

  /**
   * Attach the reserved traits to whoever is current, without an identify.
   *
   * This does NOT make the person identified: only `identify()` does that, so a
   * trait set on an anonymous visitor leaves them anonymous and carrying an
   * address, which is exactly what a newsletter signup before login looks like.
   */
  setPersonTraits(traits: PersonTraits): void {
    const set = traitProperties(traits);
    if (Object.keys(set).length === 0) return;
    this.capture(SYSTEM_EVENT.set, { properties: { $set: set } });
  }

  async getAllFlags(): Promise<Record<string, boolean>> {
    const distinctId = this.identity.getDistinctId();
    const decisions = await this.flagsClient.fetchDecisions(distinctId);
    this.cachedFlags = { distinctId, flags: decisions.flags, variants: decisions.variants ?? {} };
    return decisions.flags;
  }

  async isFeatureEnabled(key: string): Promise<boolean> {
    const flags = await this.getAllFlags();
    const enabled = flags[key] ?? false;
    this.recordExposure(key, this.cachedFlags?.variants[key]?.key ?? String(enabled));
    return enabled;
  }

  /** The assigned variation for one flag, or null when the flag is unknown or
   *  carries no variations. Records the exposure either way. */
  async getVariant(key: string): Promise<FlagVariant | null> {
    const flags = await this.getAllFlags();
    const variant = this.cachedFlags?.variants[key] ?? null;
    this.recordExposure(key, variant?.key ?? String(flags[key] ?? false));
    return variant;
  }

  async getVariantLocal(key: string, context: LocalFlagContext = {}): Promise<FlagVariant | null> {
    const snapshot = await this.flagSnapshot();
    const rule = snapshot.flags.find(candidate => candidate.key === key);
    const ctx = await this.localContext(context, snapshot.flags);
    const assigned = rule ? assignVariantIn(rule, snapshot.flags, ctx) : null;
    const enabled = rule ? evaluateFlagIn(rule, snapshot.flags, ctx).enabled : false;
    this.recordExposure(key, assigned?.key ?? String(enabled));
    return assigned ? { key: assigned.key, value: assigned.value } : null;
  }

  private recordExposure(key: string, response: string): void {
    const marker = `${this.identity.getDistinctId()} ${key} ${response}`;
    if (this.exposures.has(marker)) {
      return;
    }
    this.exposures.add(marker);
    this.capture(SYSTEM_EVENT.featureFlagCalled, { properties: { $feature_flag: key, $feature_flag_response: response } });
  }

  getCachedFlag(key: string): boolean | undefined {
    if (!this.cachedFlags || this.cachedFlags.distinctId !== this.distinctId) {
      return undefined;
    }
    return this.cachedFlags.flags[key];
  }

  async getLocalFlags(context: LocalFlagContext = {}): Promise<Record<string, boolean>> {
    const snapshot = await this.flagSnapshot();
    return evaluateAll(snapshot.flags, await this.localContext(context, snapshot.flags));
  }

  async isFeatureEnabledLocal(key: string, context: LocalFlagContext = {}): Promise<boolean> {
    const snapshot = await this.flagSnapshot();
    const rule = snapshot.flags.find(candidate => candidate.key === key);
    const ctx = await this.localContext(context, snapshot.flags);
    const enabled = rule ? evaluateFlagIn(rule, snapshot.flags, ctx).enabled : false;
    this.recordExposure(key, (rule ? assignVariantIn(rule, snapshot.flags, ctx)?.key : undefined) ?? String(enabled));
    return enabled;
  }

  /** The evaluation context for a local read. Memberships come from the caller
   *  when supplied, else from the server once per distinct id per snapshot
   *  TTL, and only when the snapshot names a cohort at all. */
  private async localContext(context: LocalFlagContext, rules: FlagRule[]): Promise<FlagEvalContext> {
    const distinctId = this.identity.getDistinctId();
    let cohorts = context.cohorts;
    if (cohorts === undefined && snapshotNeedsCohorts(rules)) {
      const now = this.config.clock.now().getTime();
      if (this.membershipCache && this.membershipCache.distinctId === distinctId && now - this.membershipCache.fetchedAt < SNAPSHOT_TTL_MS) {
        cohorts = this.membershipCache.cohorts;
      } else {
        cohorts = await this.flagsClient.memberships(distinctId);
        this.membershipCache = { cohorts, distinctId, fetchedAt: now };
      }
    }
    return { attributes: context.attributes, cohorts, distinctId, properties: context.properties };
  }

  private async flagSnapshot(): Promise<FlagSnapshot> {
    const now = this.config.clock.now().getTime();
    if (this.cachedSnapshot && now - this.cachedSnapshot.fetchedAt < SNAPSHOT_TTL_MS) {
      return this.cachedSnapshot.snapshot;
    }
    const snapshot = await this.flagsClient.snapshot();
    this.cachedSnapshot = { fetchedAt: now, snapshot };
    this.maybeStream(snapshot);
    return snapshot;
  }

  /** Opens the flag stream once, when streaming was asked for and the snapshot
   *  names a gateway. A pushed change drops the cached snapshot and refetches
   *  it; a refused handshake stays closed, a dropped one reopens. */
  private maybeStream(snapshot: FlagSnapshot): void {
    if (!this.config.streamFlags || this.shutDown || this.stream || !snapshot.socketUrl) {
      return;
    }
    const stream = new FlagStream({
      host: snapshot.socketUrl,
      onChange: () => {
        this.cachedSnapshot = undefined;
        this.flagSnapshot().catch(error => this.config.onError(error));
      },
      onClosed: retryable => {
        this.stream = undefined;
        if (retryable) {
          this.maybeStream(snapshot);
        }
      },
      projectKey: this.config.apiKey,
      socketFactory: this.config.socketFactory,
    });
    this.stream = stream;
    stream.open();
  }

  async getInbox(): Promise<InboxMessage[]> {
    return this.messagesClient.inbox(this.identity.getDistinctId());
  }

  async markMessageRead(messageId: string): Promise<void> {
    await this.messagesClient.markRead(messageId, this.identity.getDistinctId());
  }

  async flush(): Promise<void> {
    if (this.flushing || this.queue.size === 0) {
      return;
    }
    this.flushing = true;
    const events = this.queue.drain(SERVER_BATCH_LIMIT);
    try {
      await this.transport.send({
        body: {
          events,
          sent_at: isoTimestamp(this.config.clock.now()),
        },
        projectKey: this.config.apiKey,
        url: `${this.config.host}/v1/events`,
      });
      this.persistQueue();
    } catch (error) {
      this.queue.requeue(events);
      this.persistQueue();
      this.config.onError(error);
    } finally {
      this.flushing = false;
    }
  }

  reset(): void {
    this.session?.reset();
    this.identity.reset();
    this.cachedFlags = undefined;
    this.cachedSnapshot = undefined;
    this.membershipCache = undefined;
  }

  async shutdown(): Promise<void> {
    this.shutDown = true;
    this.stopTimer();
    this.stream?.close();
    this.stream = undefined;
    await this.flush();
  }

  private enqueue(envelope: EventEnvelope): void {
    this.queue.enqueue(envelope);
    this.persistQueue();
    if (this.queue.size >= this.config.flushAt) {
      void this.flush();
    }
  }

  private persistQueue(): void {
    try {
      this.config.storage.setItem(this.persistKey(), JSON.stringify(this.queue.snapshot()));
    } catch (error) {
      this.config.onError(error);
    }
  }

  private restoreQueue(): void {
    try {
      const raw = this.config.storage.getItem(this.persistKey());
      if (!raw) {
        return;
      }
      const events = JSON.parse(raw) as EventEnvelope[];
      if (Array.isArray(events)) {
        this.queue.restore(events);
      }
    } catch (error) {
      this.config.onError(error);
    }
  }

  private startTimer(): void {
    if (this.config.flushIntervalMs <= 0) {
      return;
    }
    const timer = setInterval(() => {
      void this.flush();
    }, this.config.flushIntervalMs);
    const unrefable = timer as { unref?: () => void };
    if (typeof unrefable.unref === 'function') {
      unrefable.unref();
    }
    this.flushTimer = timer;
  }

  private stopTimer(): void {
    if (this.flushTimer !== undefined) {
      clearInterval(this.flushTimer);
      this.flushTimer = undefined;
    }
  }

  private persistKey(): string {
    return `pdx.${this.config.namespace}.${PERSIST_SUFFIX}`;
  }
}

export function createClient(config: ProdantixConfig): ProdantixClient {
  return new ProdantixClient(config);
}
